import { reaction, makeAutoObservable } from 'mobx';
import memoize from 'lodash.memoize';
import i18n from 'i18next';
import ChainedBackend from 'i18next-chained-backend';
import resourcesToBackend from 'i18next-resources-to-backend';
import { useTranslation, initReactI18next } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/de';
import 'dayjs/locale/de-at';
import 'dayjs/locale/de-ch';
import 'dayjs/locale/en';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/en-au';
import 'dayjs/locale/en-ca';
import 'dayjs/locale/en-ie';
import 'dayjs/locale/en-il';
import 'dayjs/locale/en-nz';
import 'dayjs/locale/en-sg';
import 'dayjs/locale/es';
import 'dayjs/locale/es-do';
import 'dayjs/locale/es-us';
import 'dayjs/locale/fi';
import 'dayjs/locale/fr';
import 'dayjs/locale/fr-ca';
import 'dayjs/locale/fr-ch';
import 'dayjs/locale/it';
import 'dayjs/locale/it-ch';
import 'dayjs/locale/zh';
import 'dayjs/locale/zh-cn';
import 'dayjs/locale/zh-hk';
import 'dayjs/locale/zh-tw';
import { DayOfWeek } from '../../Model/DayOfWeek';
import { logger } from '../../Utils/logger';
import { RemoteSettingsStore } from '../../Services/Firebase/RemoteSettingsStore';
import { CURRENCIES, startsOnFriday, startsOnSaturday, startsOnSunday, WEEK } from '../../Utils/CustomLocalization';
import Localization, { TemperatureUnit } from '../../Utils/Localization';
import __DEV__ from '../../Utils/Dev';
import AppStore from '../../Services/AppStore';
import { languageState } from '../../Contexts/LanguageContext';
import Backend from "../../Services/Http/Backend";
import I18NextHttpBackend from "i18next-http-backend";
import { HttpBackend } from "../../Services/Http/HttpBackend";
import enTranslations from '../../Assets/Locales/Languages/en/translations.json';
import deTranslations from '../../Assets/Locales/Languages/de/translations.json';
import frTranslations from '../../Assets/Locales/Languages/fr/translations.json';
import itTranslations from '../../Assets/Locales/Languages/it/translations.json';

export function useTrans() {
  const { t, ready, i18n } = useTranslation('coach-web');
  return { t, i18n, ready };
}

const translationGetters: { [key: string]: () => any } = {
  en: () => enTranslations,
  de: () => deTranslations,
  fr: () => frTranslations,
  it: () => itTranslations,
};

const dayjsLocales: string[] = [
  'de', 'de-at', 'de-ch', 'en', 'en-ch', 'en-gb', 'en-au', 'en-ca', 'en-ie',
  'en-il', 'en-nz', 'en-sg', 'es', 'es-do', 'es-us', 'fi', 'fr', 'fr-ca',
  'fr-ch', 'it', 'it-ch', 'zh', 'zh-cn', 'zh-hk', 'zh-tw',
];

const fallback = { languageTag: 'en', isRTL: false };

export const translate = memoize(
  (key: string, config?: any) => i18n.t(key, config),
  (key: string, config?: any) => key + JSON.stringify(config ?? {}),
);

function createLocalizationStore(remoteSettings: RemoteSettingsStore) {
  const store = makeAutoObservable({
    remoteSettings,
    isRTL: false,
    use24HoursClock: true,
    country: 'CH',
    _useMetricSystem: true,
    temperatureUnit: 'celsius' as TemperatureUnit,
    numberFormat: {
      decimalSeparator: '.',
      groupingSeparator: "'",
    },
    timeZone: 'Europe/Zurich',
    currencies: ['CHF'],
    calendar: 'gregorian',

    changeLanguage(language: string = 'en') {
      languageState.setLanguage(language);
      AppStore.store('language', language);
      this.changeDayJsLanguage(language);
      return Promise.all([i18n.changeLanguage(language), this.remoteSettings.saveSetting('language', language)]);
    },

    get timezoneOffset(): number {
      const value = new Date().getTimezoneOffset();
      if (value > 0) {
        return -value;
      } else {
        return Math.abs(value);
      }
    },

    initialize(language?: string) {
    // fallback if no available language fits

    logger('LocalizationStore::setI18nConfig', language);

    const { languageTag, isRTL } = Localization.findBestAvailableLanguage(Object.keys(translationGetters)) || fallback;
    const languageCode = language || languageTag || 'en';

    // clear translation cache
    translate.cache.clear && translate.cache.clear();
    // update layout direction
    // I18nManager.forceRTL(isRTL);

    i18n
      .use(initReactI18next)
      .use(ChainedBackend)
      .init({
        debug: __DEV__,
        defaultNS: 'coach-web',
        ns: 'coach-web',
        load: 'languageOnly',
        nsSeparator: '::',
        lng: languageCode,
        fallbackLng: 'en',
        saveMissing: __DEV__,
        interpolation: {
          escapeValue: false,
        },
        backend: {
          backends: [
            I18NextHttpBackend,
            resourcesToBackend((lang, ns, callback) => {
              const resources = translationGetters[lang]();
              callback(null, resources);
              // import(`../../Assets/Locales/Languages/${lang}/translations.json`)
              //   .then((res) => callback(null, res))
              //   .catch((e) => callback(e, undefined))
            }),
          ],
          backendOptions: [
            {
              version: 'latest',
              referenceLng: 'en',
              namespace: 'coach-web',
              allowMultiLoading: false,
              loadPath: '/locales/{{lng}}/{{ns}}',
              addPath: '/locales/{{lng}}/{{ns}}',
              queryStringParams: { version: 'latest' },
              request: (options, url, payload, callback) => {
                logger('translation url', url, options, payload);
                if (payload) {
                  HttpBackend.post(`/messaging/translation${url}`, payload)
                    .then((res) => {
                      logger('translations', res.translations);
                      callback(null, { status: 200, data: JSON.stringify(res.translations) });
                    })
                    .catch((err) => {
                      console.error(err);
                    });
                } else {
                  new Backend().get(`/messaging/translation${url}`, options.queryStringParams)
                    .then((res) => {
                      logger('translations', res.translations);
                      callback(null, { status: 200, data: JSON.stringify(res.translations) });
                    })
                    .catch((err) => callback(err));
                }
              },
            },
            {
              // prefix for stored languages
              prefix: 'i18next_res_',
              // expiration
              expirationTime: 24 * 60 * 60 * 1000,
              // language versions
              versions: {},
            },
          ],
        },
        missingKeyHandler: (lngs, ns, key, fallbackValue, updateMissing, options) => {
          logger('missing key', lngs, ns, key, fallbackValue, updateMissing, options);
          HttpBackend.post(`/messaging/translation/locales/${lngs}/${ns}`, {
            [key]: fallbackValue ?? options.defaultValue ?? '',
          }).catch((err) => {
            console.error(err);
          });

          return options.defaultValue ?? fallbackValue ?? `[${key}]`;
        },
        });

      this.changeDayJsLanguage(languageCode);
      this.updateObservables(languageCode, isRTL);
    },

    changeDayJsLanguage(languageCode: string) {
      const dayjsLanguage = dayjsLocales.includes(languageCode)
        ? languageCode
        : dayjsLocales.includes(languageCode.toLowerCase())
          ? languageCode.toLowerCase()
          : 'en';
      dayjs.locale(dayjsLanguage);
    },

    updateObservables(languageTag: string, isRTL: boolean) {
      languageState.setLanguage(languageTag);
      this.isRTL = isRTL;
      this._useMetricSystem = Localization.usesMetricSystem() ?? true;
      this.use24HoursClock = Localization.uses24HourClock();
      this.temperatureUnit = Localization.getTemperatureUnit();
      this.country = Localization.getCountry();
      this.currencies = Localization.getCurrencies();
      this.numberFormat = Localization.getNumberFormatSettings();
      this.timeZone = Localization.getTimeZone();
      this.calendar = Localization.getCalendar();
    },

    get currency(): string {
      if (this.currencies.length > 0) {
        return this.currencies[0];
      }
      return 'CHF';
    },

    get useMetricSystem(): boolean {
      const unitSystem = this.remoteSettings.get('unit_system');
      logger('useMetricSystem', unitSystem);
      if (unitSystem && typeof unitSystem === 'string') {
        return unitSystem !== 'imperial';
      }
      return this._useMetricSystem;
    },

    get firstDayOfWeek(): DayOfWeek {
      if (startsOnFriday.includes(this.country)) {
        return 'FRIDAY';
      } else if (startsOnSaturday.includes(this.country)) {
        return 'SATURDAY';
      } else if (startsOnSunday.includes(this.country)) {
        return 'SUNDAY';
      }
      return 'MONDAY';
    },

    get week(): DayOfWeek[] {
      const week: DayOfWeek[] = [];
      const startIndex = WEEK.indexOf(this.firstDayOfWeek);
      for (let i = startIndex; i < 7 + startIndex; i += 1) {
        week.push(WEEK[i % WEEK.length]);
      }
      return week;
    },

    get language(): string {
      return languageState.language;
    },

    handleLocalizationChange() {
      logger('locale changed');
      this.initialize();
    },
  });

  store.initialize(AppStore.get('language'));

  reaction(
    () => true,
    (loaded) => {
      if (loaded) {
        logger('setting localization settings');
        remoteSettings
          .setMany({
            locales: Localization.getLocales(),
            timezone: Localization.getTimeZone(),
            currencies: Localization.getCurrencies(),
            calendar: Localization.getCalendar(),
            uses24HourClock: Localization.uses24HourClock(),
            usesMetricSystem: Localization.usesMetricSystem(),
            temperatureUnit: Localization.getTemperatureUnit(),
            numberFormat: Localization.getNumberFormatSettings(),
          })
          .catch(() => {
            console.error('failed to save settings');
          });
      }
    },
    { fireImmediately: true, name: 'Save timezone' },
  );

  Localization.addEventListener('change', () => store.handleLocalizationChange());

  return store;
}

export type LocalizationStore = ReturnType<typeof createLocalizationStore>;
export const createLocalizationStoreInstance = createLocalizationStore;
